import { useEffect, useMemo, useRef, useState } from "react";
import {
  useAuditLog,
  useListProjectFiles,
  useLiveSandboxExec,
  useProjectBuilds,
  usePublishStatus,
  useReadProjectFile,
  useSandbox,
  useSandboxExec,
  API_BASE,
  type AuditEvent,
} from "@vorloop/api-client-react";
import type { CenterTab, DeviceMode } from "./TopBar";
import { BrowserFrame, PhoneFrame } from "./PhoneFrame";
import { CanvasBoard } from "./CanvasBoard";

/** Default port we point the live-preview proxy at. Users running
 *  Vite (5173) or Expo web (19006) need to either change their dev
 *  server's port flag or override via ?vorPort=N when pasting URLs. */
const DEFAULT_LIVE_PREVIEW_PORT = 3000;

/**
 * Center pane. Loads the project's preview inside the iPhone frame
 * (ios mode) or browser chrome (web mode).
 *
 * Two preview surfaces:
 *   - `/preview/expo/`: bundles app/(tabs)/index.tsx etc. via the
 *     server-side esbuild + react-native-web pipeline. Used when the
 *     project has a mobile entry file.
 *   - `/preview/`: serves /index.html directly. Used for web projects
 *     and as fallback for mobile projects that don't have an RN entry
 *     yet (so the agent's HTML scaffold still shows).
 *
 * `previewSeq` lets the parent force-refresh after agent edits — we
 * bust the iframe by changing src.
 */
export function CanvasPane({
  projectId,
  projectName,
  projectType,
  hasMobileEntry,
  tab,
  device,
  previewSeq = 0,
  onChangeTab,
}: {
  projectId: string;
  projectName: string;
  projectType: "mobile" | "web" | "component" | "backend";
  hasMobileEntry: boolean;
  tab: CenterTab;
  device: DeviceMode;
  previewSeq?: number;
  /** Launcher → top-level tab: lets the user open Database / Secrets /
   * Audit / Domains / Canvas as their own tab in the strip. */
  onChangeTab?: (t: CenterTab) => void;
}) {
  const useExpoPreview = projectType === "mobile" && hasMobileEntry;
  const sandbox = useSandbox(projectId);
  const sandboxRunning =
    sandbox.data?.sandbox?.status === "running" ||
    sandbox.data?.sandbox?.status === "starting";
  // Static preview (Expo-bundled or HTML) is always the default. A
  // running sandbox doesn't imply a dev server is up — the user has
  // to `npm run dev` themselves first. The Live toggle below is the
  // explicit opt-in once they have one running.
  const [liveMode, setLiveMode] = useState(false);
  const showLive = liveMode && sandboxRunning;
  //
  // ALL three branches must be absolute against API_BASE in prod —
  // the SPA lives on vor.codes but the API + preview endpoints live on
  // api.vor.codes. Without the prefix the iframe loads `/api/...` from
  // vor.codes, Cloudflare Pages falls back to index.html, wouter
  // doesn't match `/api/...`, and the iframe renders the SPA's own
  // "Not found" page. API_BASE is "" in dev (Vite proxy handles it).
  const previewUrl = showLive
    ? `${API_BASE}/api/projects/${projectId}/preview/live/?vorPort=${DEFAULT_LIVE_PREVIEW_PORT}&v=${previewSeq}`
    : useExpoPreview
      ? `${API_BASE}/api/projects/${projectId}/preview/expo/?v=${previewSeq}`
      : `${API_BASE}/api/projects/${projectId}/preview/?v=${previewSeq}`;

  if (tab === "domains") {
    return <DomainsPanel projectId={projectId} />;
  }

  if (tab === "audit") {
    return <AuditPanel projectId={projectId} />;
  }

  if (tab === "database") {
    return <DatabasePanel />;
  }

  if (tab === "secrets") {
    return <SecretsPanel projectId={projectId} />;
  }

  if (tab === "tools") {
    return <ToolsLauncher projectId={projectId} onOpenCenterTab={(t) => onChangeTab?.(t)} />;
  }

  if (tab === "canvas") {
    return (
      <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
        <CanvasBoard projectId={projectId} projectName={projectName} previewUrl={previewUrl} />
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: "var(--bg-canvas)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: 24,
      }}
    >
      {device === "ios" ? (
        <>
          <PhoneFrame>
            {tab === "preview" ? (
              <PreviewIframe src={previewUrl} title="App preview (mobile)" />
            ) : (
              <DesignPlaceholder tab={tab} />
            )}
          </PhoneFrame>
          <PreviewActionBar
            sandboxRunning={sandboxRunning}
            liveMode={liveMode}
            onToggleLive={() => setLiveMode((v) => !v)}
          />
        </>
      ) : (
        <>
          <BrowserFrame url={`https://${projectId}.preview.vor.codes/`}>
            {tab === "preview" ? (
              <PreviewIframe src={previewUrl} title="App preview (web)" />
            ) : (
              <DesignPlaceholder tab={tab} />
            )}
          </BrowserFrame>
          <PreviewActionBar
            sandboxRunning={sandboxRunning}
            liveMode={liveMode}
            onToggleLive={() => setLiveMode((v) => !v)}
          />
        </>
      )}
    </div>
  );
}

function PreviewActionBar({
  sandboxRunning,
  liveMode,
  onToggleLive,
}: {
  sandboxRunning: boolean;
  liveMode: boolean;
  onToggleLive: () => void;
}) {
  // Bottom toolbar that routes every action through the chat. Draw +
  // Edit live in the Canvas tab (TopBar → "Canvas") which has the
  // overlay tooling; from here we just nudge the user there or open
  // the chat with a prefilled prompt.
  function on(label: string) {
    return () => {
      const prompts: Record<string, string> = {
        "Request changes": "I want to request changes to the current preview — ",
        Chat: "About this preview: ",
        Draw: "Switch to the Canvas tab (top bar) — Draw lives there with overlay tooling.",
        Edit: "Switch to the Canvas tab (top bar) — Edit lives there with element selection.",
        "+ Generate": "Generate a new component for this project — ",
      };
      const prompt = prompts[label] ?? `${label}: `;
      if (label === "Draw" || label === "Edit") {
        // No good way to programmatically switch tabs from here; alert
        // is the honest, minimum-surprise nudge until we wire a router.
        alert(prompt);
      } else {
        window.dispatchEvent(new CustomEvent("vp:chat:prefill", { detail: { prompt } }));
      }
    };
  }
  return (
    <div
      role="toolbar"
      aria-label="Preview actions"
      data-testid="preview-action-bar"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: 4,
        background: "var(--bg-panel)",
        border: "1px solid var(--border-default)",
        borderRadius: 999,
        boxShadow: "0 6px 24px -6px rgba(0,0,0,0.4)",
      }}
    >
      <button
        type="button"
        onClick={on("Request changes")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          background: "transparent",
          color: "var(--text-secondary)",
          border: "none",
          borderRadius: 999,
          fontSize: 12,
          cursor: "pointer",
        }}
      >
        <span aria-hidden>↩</span>
        <span>Request changes</span>
      </button>
      <span aria-hidden style={{ width: 1, height: 18, background: "var(--border-default)" }} />
      <button
        type="button"
        onClick={onToggleLive}
        disabled={!sandboxRunning}
        title={sandboxRunning
          ? (liveMode
              ? "Proxying to the sandbox dev server on port 3000. Switch off if no server is running."
              : "Proxy iframe to the dev server inside the sandbox. Run `npm run dev -- --host 0.0.0.0 -p 3000` in the terminal first.")
          : "Start a sandbox in the top bar to enable live preview."}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          background: liveMode ? "rgba(34,197,94,0.18)" : "transparent",
          color: liveMode
            ? "#34d399"
            : sandboxRunning
              ? "var(--text-secondary)"
              : "var(--text-tertiary)",
          border: "none",
          borderRadius: 999,
          fontSize: 12,
          cursor: sandboxRunning ? "pointer" : "not-allowed",
          opacity: sandboxRunning ? 1 : 0.5,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: liveMode ? "#22c55e" : "var(--text-tertiary)",
          }}
        />
        <span>{liveMode ? "Live" : "Static"}</span>
      </button>
      <span aria-hidden style={{ width: 1, height: 18, background: "var(--border-default)" }} />
      {[
        { id: "chat", label: "Chat", icon: "💬" },
        { id: "draw", label: "Draw", icon: "✏️" },
        { id: "edit", label: "Edit", icon: "✎" },
        { id: "generate", label: "+ Generate", icon: "✨", primary: true as const },
      ].map((b) => (
        <button
          key={b.id}
          type="button"
          onClick={on(b.label)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: b.primary ? "var(--accent-blue)" : "transparent",
            color: b.primary ? "#fff" : "var(--text-primary)",
            border: "none",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <span aria-hidden>{b.icon}</span>
          <span>{b.label}</span>
        </button>
      ))}
    </div>
  );
}

function DomainsPanel({ projectId }: { projectId: string }) {
  const publish = usePublishStatus(projectId);
  const subdomain = publish.data?.slug
    ? `${publish.data.slug}.preview.vor.codes`
    : `${projectId.slice(0, 8)}.preview.vor.codes (publish to claim)`;

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: "var(--bg-canvas)",
        padding: "40px 32px",
        overflowY: "auto",
        color: "var(--text-primary)",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
        <header>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Domains</h1>
          <p style={{ color: "var(--text-tertiary)", fontSize: 13, marginTop: 6 }}>
            Where your project is reachable.
          </p>
        </header>
        <section
          style={{
            border: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
            borderRadius: 12,
            padding: 18,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 500, fontSize: 13 }}>Preview subdomain</span>
            <span style={statusPill(publish.data?.slug ? "active" : "pending")}>
              {publish.data?.slug ? "active" : "unclaimed"}
            </span>
          </div>
          <code
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--text-secondary)",
              padding: "8px 10px",
              borderRadius: 8,
              background: "var(--bg-canvas)",
              border: "1px solid var(--border-default)",
              display: "block",
            }}
          >
            https://{subdomain}
          </code>
          <p style={{ color: "var(--text-tertiary)", fontSize: 11, margin: 0 }}>
            Live URL once you've published the project at least once.
          </p>
        </section>
        <ComingSoonCard description="Visibility toggle (public / private) + custom domain attachment (CNAME + Cloudflare SSL-for-SaaS) land alongside Phase 3 billing — they need a slugs.visibility column and the custom_domains table wired through publish-store list and PATCH endpoints." />
      </div>
    </div>
  );
}

function AuditPanel({ projectId }: { projectId: string }) {
  const log = useAuditLog(projectId);
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: "var(--bg-canvas)",
        padding: "40px 32px",
        overflowY: "auto",
        color: "var(--text-primary)",
      }}
    >
      <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
        <header>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Audit log</h1>
          <p style={{ color: "var(--text-tertiary)", fontSize: 13, marginTop: 6 }}>
            Recent activity across this project. Owner-only.
          </p>
        </header>

        {log.error && (
          <div
            style={{
              padding: 12,
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.4)",
              borderRadius: 8,
              color: "var(--accent-red)",
              fontSize: 12,
            }}
          >
            {log.error.message}
          </div>
        )}

        {log.isLoading && (
          <div style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Loading…</div>
        )}

        {!log.isLoading && (log.data?.items.length ?? 0) === 0 && !log.error && (
          <div
            style={{
              padding: 24,
              border: "1px dashed var(--border-default)",
              borderRadius: 12,
              color: "var(--text-tertiary)",
              fontSize: 13,
              textAlign: "center",
            }}
          >
            No activity yet.
          </div>
        )}

        <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {log.data?.items.map((evt: AuditEvent, i: number) => (
            <li
              key={i}
              style={{
                padding: "10px 12px",
                background: "var(--bg-panel)",
                border: "1px solid var(--border-default)",
                borderRadius: 8,
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <span style={kindPill(evt.kind)}>{kindLabel(evt.kind)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: "var(--text-primary)" }}>{evt.summary}</div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>
                  {evt.actor ? `${evt.actor} · ` : ""}{new Date(evt.at).toLocaleString()}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function kindLabel(kind: string): string {
  switch (kind) {
    case "agent_turn":
      return "Agent";
    case "member_added":
      return "Member";
    case "invite_created":
      return "Invite";
    case "invite_accepted":
      return "Accept";
    default:
      return kind;
  }
}

function kindPill(kind: string): React.CSSProperties {
  const map: Record<string, { bg: string; fg: string }> = {
    agent_turn: { bg: "rgba(99,102,241,0.12)", fg: "#a5b4fc" },
    member_added: { bg: "rgba(16,185,129,0.12)", fg: "#34d399" },
    invite_created: { bg: "rgba(245,158,11,0.12)", fg: "#fbbf24" },
    invite_accepted: { bg: "rgba(16,185,129,0.12)", fg: "#34d399" },
  };
  const c = map[kind] ?? { bg: "rgba(107,114,128,0.18)", fg: "#cbd5e1" };
  return {
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    background: c.bg,
    color: c.fg,
    borderRadius: 999,
    flexShrink: 0,
    marginTop: 2,
  };
}

function statusPill(state: "active" | "pending" | "error"): React.CSSProperties {
  const map = {
    active: { bg: "rgba(16,185,129,0.12)", fg: "#34d399" },
    pending: { bg: "rgba(245,158,11,0.12)", fg: "#fbbf24" },
    error: { bg: "rgba(239,68,68,0.12)", fg: "#f87171" },
  } as const;
  const { bg, fg } = map[state];
  return {
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    background: bg,
    color: fg,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  };
}

function PreviewIframe({ src, title }: { src: string; title: string }) {
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const ref = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    setLoading(true);
  }, [src]);

  // Listen for the composer's start/cancel events. When selecting,
  // install a click + hover inspector inside the iframe's document
  // (same-origin since preview is served by our API). On click,
  // resolve the selection and dispatch back to the composer.
  useEffect(() => {
    function onStart() {
      setSelecting(true);
    }
    function onCancel() {
      setSelecting(false);
    }
    window.addEventListener("vp:select-element:start", onStart);
    window.addEventListener("vp:select-element:cancel", onCancel);
    return () => {
      window.removeEventListener("vp:select-element:start", onStart);
      window.removeEventListener("vp:select-element:cancel", onCancel);
    };
  }, []);

  useEffect(() => {
    if (!selecting) return;
    const frame = ref.current;
    if (!frame) return;
    let doc: Document | null = null;
    try {
      doc = frame.contentDocument ?? frame.contentWindow?.document ?? null;
    } catch {
      doc = null;
    }
    if (!doc) {
      // Cross-origin (prod: vor.codes ↔ api.vor.codes). Talk to the
      // iframe's injected runtime via postMessage instead. See the
      // <script> in expoWrapperHtml on the api-server side.
      const win = frame.contentWindow;
      if (!win) return;
      function onMessage(e: MessageEvent) {
        const d = e.data;
        if (!d || typeof d !== "object") return;
        if (d.type === "vor:select:resolved") {
          // Iframe now ships an array of picks (multi-select). Fall back
          // to the legacy single-selector shape for forward-compat.
          const items: Array<{ selector: string; text?: string }> = Array.isArray(d.items)
            ? d.items
            : typeof d.selector === "string"
              ? [{ selector: d.selector, text: d.text }]
              : [];
          if (items.length > 0) {
            window.dispatchEvent(
              new CustomEvent("vp:select-element:resolve", { detail: { items } }),
            );
          }
        } else if (d.type === "vor:select:cancel") {
          window.dispatchEvent(new CustomEvent("vp:select-element:cancel"));
        }
      }
      window.addEventListener("message", onMessage);
      try { win.postMessage({ type: "vor:select:start" }, "*"); } catch { /* iframe gone */ }
      return () => {
        window.removeEventListener("message", onMessage);
        try { win.postMessage({ type: "vor:select:cancel" }, "*"); } catch { /* ignore */ }
      };
    }
    const root = doc;
    let lastHover: HTMLElement | null = null;
    function clearHover() {
      if (lastHover) {
        lastHover.style.outline = "";
        lastHover.style.outlineOffset = "";
      }
      lastHover = null;
    }
    function setHover(el: HTMLElement) {
      if (lastHover === el) return;
      clearHover();
      lastHover = el;
      el.style.outline = "2px solid #6366f1";
      el.style.outlineOffset = "-2px";
    }
    function buildSelector(el: HTMLElement): string {
      const tag = el.tagName.toLowerCase();
      const id = el.id ? `#${el.id}` : "";
      const cls = el.classList[0] ? `.${el.classList[0]}` : "";
      return `${tag}${id}${cls}`;
    }
    function onOver(e: Event) {
      const t = e.target as HTMLElement | null;
      if (!t || !t.style) return;
      setHover(t);
    }
    function onClick(e: Event) {
      e.preventDefault();
      e.stopPropagation();
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const selector = buildSelector(t);
      const text = (t.textContent || "").trim().slice(0, 120);
      clearHover();
      // Dev / same-origin path is single-shot. Multi-select is the
      // cross-origin code path (postMessage) which has its own Done UI.
      window.dispatchEvent(
        new CustomEvent("vp:select-element:resolve", { detail: { items: [{ selector, text }] } }),
      );
      setSelecting(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        clearHover();
        window.dispatchEvent(new CustomEvent("vp:select-element:cancel"));
        setSelecting(false);
      }
    }
    root.addEventListener("mouseover", onOver, true);
    root.addEventListener("click", onClick, true);
    root.addEventListener("keydown", onKey, true);
    // Visual cue: tint the cursor.
    const prevCursor = root.documentElement.style.cursor;
    root.documentElement.style.cursor = "crosshair";
    return () => {
      clearHover();
      root.removeEventListener("mouseover", onOver, true);
      root.removeEventListener("click", onClick, true);
      root.removeEventListener("keydown", onKey, true);
      root.documentElement.style.cursor = prevCursor;
    };
  }, [selecting, loading]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <iframe
        ref={ref}
        src={src}
        title={title}
        // No sandbox attr: we serve our own content from the same origin
        // and need scripts to run. With both allow-scripts and
        // allow-same-origin set the sandbox provides no protection
        // anyway — Chrome correctly warns about it.
        onLoad={() => setLoading(false)}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          background: "transparent",
          display: "block",
        }}
      />
      {selecting && (
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            right: 8,
            padding: "6px 10px",
            background: "rgba(99,102,241,0.95)",
            color: "#fff",
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 500,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            boxShadow: "0 6px 18px -4px rgba(0,0,0,0.35)",
          }}
        >
          <span>🎯 Click an element to select · Esc to cancel</span>
        </div>
      )}
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            color: "var(--text-tertiary)",
            fontSize: 11,
            background: "var(--bg-canvas)",
            pointerEvents: "none",
          }}
        >
          Loading preview…
        </div>
      )}
    </div>
  );
}

function DatabasePanel() {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: "var(--bg-canvas)",
        padding: "32px",
        overflowY: "auto",
        color: "var(--text-primary)",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
        <header>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Database</h1>
          <p style={{ color: "var(--text-tertiary)", fontSize: 13, marginTop: 6, lineHeight: 1.6 }}>
            Per-project Postgres is not provisioned yet. To use a real DB today, add a connection string to the project's Secrets (right panel → Tools → Secrets) as <code>DATABASE_URL</code> and the sandbox can connect via your Neon / Supabase / Railway account.
          </p>
          <p style={{ color: "var(--text-tertiary)", fontSize: 13, marginTop: 10, lineHeight: 1.6 }}>
            One-click "create a Vor Postgres for this project" lands alongside Phase 3 billing — it provisions a Neon branch per project and pipes the connection string in automatically.
          </p>
        </header>
        <section
          style={{
            border: "1px dashed var(--border-strong)",
            background: "rgba(255,255,255,0.02)",
            borderRadius: 12,
            padding: 20,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>BYO database (works today)</div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "var(--text-tertiary)", lineHeight: 1.7 }}>
            <li>Create a free Neon project at neon.tech (or Supabase / Railway).</li>
            <li>Copy its <code>postgres://...</code> connection string.</li>
            <li>Paste into Secrets → <code>DATABASE_URL</code>.</li>
            <li>From the terminal: <code>npm install pg</code>, then your app reads <code>process.env.DATABASE_URL</code>.</li>
          </ol>
        </section>
      </div>
    </div>
  );
}

interface Secret {
  readonly id: string;
  readonly key: string;
  readonly value: string;
  readonly createdAt: string;
}

function SecretsPanel({ projectId }: { projectId: string }) {
  const storageKey = `vp.secrets.${projectId}`;
  const [secrets, setSecrets] = useState<Secret[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw) as Secret[];
    } catch {
      // ignore
    }
    return [];
  });
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [keyDraft, setKeyDraft] = useState("");
  const [valueDraft, setValueDraft] = useState("");

  function persist(next: Secret[]) {
    setSecrets(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // private mode
    }
  }

  function addSecret() {
    const k = keyDraft.trim();
    const v = valueDraft;
    if (!k || !/^[A-Z][A-Z0-9_]*$/.test(k)) return;
    if (secrets.some((s) => s.key === k)) return;
    persist([
      ...secrets,
      { id: `sec_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, key: k, value: v, createdAt: new Date().toISOString() },
    ]);
    setKeyDraft("");
    setValueDraft("");
  }

  function removeSecret(id: string) {
    persist(secrets.filter((s) => s.id !== id));
  }

  function toggleReveal(id: string) {
    setRevealed((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const valid = keyDraft.trim().length > 0 && /^[A-Z][A-Z0-9_]*$/.test(keyDraft.trim());

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: "var(--bg-canvas)",
        padding: "32px",
        overflowY: "auto",
        color: "var(--text-primary)",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
        <header style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Secrets</h1>
            <p style={{ color: "var(--text-tertiary)", fontSize: 13, marginTop: 6 }}>
              Environment variables your project + the agent can read at runtime. Surfaced as{" "}
              <code>process.env.KEY_NAME</code> in the sandbox.
            </p>
          </div>
          <span
            style={{
              padding: "3px 10px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              background: "rgba(245,158,11,0.10)",
              color: "var(--accent-amber)",
              border: "1px solid rgba(245,158,11,0.35)",
            }}
          >
            Local only
          </span>
        </header>

        <section
          style={{
            border: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
            borderRadius: 12,
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              placeholder="KEY_NAME"
              value={keyDraft}
              onChange={(e) => setKeyDraft(e.target.value.toUpperCase())}
              style={{
                flex: 1,
                padding: "8px 10px",
                background: "var(--bg-canvas)",
                border: "1px solid var(--border-default)",
                borderRadius: 7,
                color: "var(--text-primary)",
                fontSize: 13,
                fontFamily: "var(--font-mono)",
                outline: "none",
              }}
            />
            <input
              type="text"
              placeholder="value"
              value={valueDraft}
              onChange={(e) => setValueDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && valid) {
                  e.preventDefault();
                  addSecret();
                }
              }}
              style={{
                flex: 2,
                padding: "8px 10px",
                background: "var(--bg-canvas)",
                border: "1px solid var(--border-default)",
                borderRadius: 7,
                color: "var(--text-primary)",
                fontSize: 13,
                fontFamily: "var(--font-mono)",
                outline: "none",
              }}
            />
            <button
              type="button"
              onClick={addSecret}
              disabled={!valid}
              style={{
                padding: "8px 14px",
                background: valid ? "var(--accent-blue)" : "var(--bg-active)",
                color: valid ? "#fff" : "var(--text-tertiary)",
                border: "none",
                borderRadius: 7,
                fontSize: 12,
                fontWeight: 600,
                cursor: valid ? "pointer" : "not-allowed",
              }}
            >
              Add secret
            </button>
          </div>
          {keyDraft.trim().length > 0 && !valid && (
            <div style={{ color: "var(--accent-red)", fontSize: 11 }}>
              Keys must be SCREAMING_SNAKE_CASE — uppercase letter first, then [A-Z0-9_].
            </div>
          )}

          {secrets.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: "var(--text-tertiary)",
                fontSize: 12,
                border: "1px dashed var(--border-default)",
                borderRadius: 8,
              }}
            >
              No secrets yet. Add KEY=value pairs above.
            </div>
          ) : (
            <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
              {secrets.map((s) => {
                const isOpen = revealed.has(s.id);
                return (
                  <li
                    key={s.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 12px",
                      background: "var(--bg-panel-2)",
                      border: "1px solid var(--border-default)",
                      borderRadius: 8,
                    }}
                  >
                    <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-primary)", minWidth: 140 }}>
                      {s.key}
                    </code>
                    <code
                      style={{
                        flex: 1,
                        fontFamily: "var(--font-mono)",
                        fontSize: 12,
                        color: "var(--text-secondary)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isOpen ? s.value : "•".repeat(Math.min(16, s.value.length || 8))}
                    </code>
                    <button
                      type="button"
                      onClick={() => toggleReveal(s.id)}
                      style={ghostBtnStyle}
                    >
                      {isOpen ? "Hide" : "Reveal"}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSecret(s.id)}
                      style={{ ...ghostBtnStyle, color: "var(--accent-red)" }}
                    >
                      Delete
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
          <p style={{ color: "var(--text-tertiary)", fontSize: 11, margin: 0 }}>
            Storage is local to this browser. When the live API ships a per-project secrets vault
            (encrypted at rest, surfaced to the sandbox env), this UI moves to it without UX changes.
          </p>
        </section>
      </div>
    </div>
  );
}

const ghostBtnStyle: React.CSSProperties = {
  padding: "4px 8px",
  background: "transparent",
  border: "1px solid var(--border-default)",
  color: "var(--text-secondary)",
  borderRadius: 6,
  fontSize: 11,
  cursor: "pointer",
};

interface ToolItem {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly category: "suggested" | "advanced" | "files";
  readonly status: "built" | "stub";
}

const TOOL_ITEMS: ReadonlyArray<ToolItem> = [
  { id: "preview", category: "suggested", status: "built", title: "Preview", description: "Preview your App" },
  { id: "publishing", category: "suggested", status: "built", title: "Publishing", description: "Publish a shareable version of your App, unaffected by changes made in the project editor" },
  { id: "integrations", category: "suggested", status: "built", title: "Integrations", description: "Connect to Replit-native and external services" },
  { id: "database", category: "suggested", status: "built", title: "Database", description: "Stores structured data such as user profiles, game scores, and product catalogs" },
  { id: "app-storage", category: "suggested", status: "built", title: "App Storage", description: "Host and save uploaded files like images, videos, and documents" },
  { id: "auth", category: "suggested", status: "built", title: "Auth", description: "Let users log in to your App using a prebuilt login page" },
  { id: "security", category: "suggested", status: "built", title: "Security Center", description: "Find, fix, and monitor security vulnerabilities and privacy issues" },
  { id: "secrets", category: "suggested", status: "built", title: "Secrets", description: "Store sensitive information (like API keys) securely in your App" },
  { id: "agent-skills", category: "suggested", status: "built", title: "Agent Skills", description: "Manage skills that extend Agent capabilities" },
  { id: "analytics", category: "suggested", status: "built", title: "Analytics", description: "View traffic, request metrics, and usage analytics for your deployed App" },
  { id: "automations", category: "suggested", status: "built", title: "Automations", description: "View and test agents and automations created by Replit Agent" },
  { id: "canvas", category: "suggested", status: "built", title: "Canvas", description: "Agent-controlled canvas for mockups and wireframes" },
  { id: "validation", category: "suggested", status: "built", title: "Validation", description: "Configure test commands and inspect run results to verify CI readiness" },
  { id: "files", category: "advanced", status: "built", title: "Files", description: "Browse the project's working tree, open or create files" },
  { id: "code-search", category: "advanced", status: "built", title: "Code Search", description: "Search through the text contents of your App" },
  { id: "console", category: "advanced", status: "built", title: "Console", description: "View the terminal output after running your code" },
  { id: "developer", category: "advanced", status: "built", title: "Developer", description: "Internal developer tools, telemetry, and diagnostics" },
  { id: "git", category: "advanced", status: "built", title: "Git", description: "Version control for your App" },
  { id: "shell", category: "advanced", status: "built", title: "Shell", description: "Directly access your App through a command line interface (CLI)" },
  { id: "vnc", category: "advanced", status: "built", title: "VNC", description: "View your app's desktop screen output" },
  { id: "workflows", category: "advanced", status: "built", title: "Workflows", description: "Configure different ways to run your App" },
];

function ToolsLauncher({ projectId, onOpenCenterTab }: { projectId: string; onOpenCenterTab?: (t: CenterTab) => void }) {
  const [query, setQuery] = useState("");
  const [openStub, setOpenStub] = useState<ToolItem | null>(null);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? TOOL_ITEMS.filter((t) => t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q))
    : TOOL_ITEMS;

  const CENTER_TAB_IDS = new Set<CenterTab>(["preview", "canvas", "design", "domains", "audit", "tools", "database", "secrets"]);

  function pickItem(t: ToolItem) {
    if (CENTER_TAB_IDS.has(t.id as CenterTab) && t.id !== "tools" && onOpenCenterTab) {
      onOpenCenterTab(t.id as CenterTab);
      return;
    }
    setOpenStub(t);
  }

  if (openStub) {
    return <ToolStubPanel projectId={projectId} item={openStub} onBack={() => setOpenStub(null)} />;
  }

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: "var(--bg-canvas)",
        padding: "24px 32px 60px",
        overflowY: "auto",
        color: "var(--text-primary)",
      }}
    >
      <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
        <input
          type="text"
          placeholder="Search for tools & files…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          style={{
            width: "100%",
            padding: "10px 14px",
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 10,
            color: "var(--text-primary)",
            fontSize: 14,
            outline: "none",
          }}
        />
        {(["suggested", "advanced"] as const).map((cat) => {
          const items = filtered.filter((t) => t.category === cat);
          if (items.length === 0) return null;
          return (
            <section key={cat} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "var(--text-tertiary)",
                  padding: "4px 6px",
                }}
              >
                {cat}
              </div>
              <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2 }}>
                {items.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => pickItem(t)}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        padding: "10px 12px",
                        width: "100%",
                        background: "transparent",
                        border: "none",
                        color: "var(--text-primary)",
                        cursor: "pointer",
                        textAlign: "left",
                        borderRadius: 8,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-panel-2)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          background: "var(--bg-panel-2)",
                          border: "1px solid var(--border-default)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--text-secondary)",
                          fontSize: 12,
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {t.title.charAt(0)}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{t.title}</span>
                          {t.status === "built" && (
                            <span
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: 0.6,
                                textTransform: "uppercase",
                                color: "#34d399",
                                background: "rgba(16,185,129,0.12)",
                                padding: "1px 6px",
                                borderRadius: 999,
                              }}
                            >
                              Open
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            display: "block",
                            color: "var(--text-tertiary)",
                            fontSize: 12,
                            marginTop: 2,
                            lineHeight: 1.45,
                          }}
                        >
                          {t.description}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function ToolStubPanel({ projectId, item, onBack }: { projectId: string; item: ToolItem; onBack: () => void }) {
  const body = panelFor(item.id, projectId);

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: "var(--bg-canvas)",
        padding: "24px 32px 60px",
        overflowY: "auto",
        color: "var(--text-primary)",
      }}
    >
      <div style={{ maxWidth: 840, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            alignSelf: "flex-start",
            padding: "5px 10px",
            background: "transparent",
            border: "1px solid var(--border-default)",
            color: "var(--text-secondary)",
            borderRadius: 7,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          ← All tools
        </button>
        <header>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{item.title}</h1>
          <p style={{ color: "var(--text-tertiary)", fontSize: 13, marginTop: 6 }}>{item.description}</p>
        </header>
        {body ?? <ComingSoonCard description={planFor(item.id)} />}
      </div>
    </div>
  );
}

function panelFor(id: string, projectId: string): React.ReactNode {
  switch (id) {
    case "workflows":
      return <WorkflowsToolPanel projectId={projectId} />;
    case "git":
      return <GitToolPanel projectId={projectId} />;
    case "developer":
      return <DeveloperToolPanel projectId={projectId} />;
    case "code-search":
      return <CodeSearchToolPanel projectId={projectId} />;
    case "vnc":
      return <VncToolPanel />;
    case "publishing":
      return <PublishingToolPanel projectId={projectId} />;
    case "integrations":
      return <IntegrationsToolPanel />;
    case "app-storage":
      return <AppStorageToolPanel projectId={projectId} />;
    case "auth":
      return <AuthToolPanel />;
    case "security":
      return <SecurityToolPanel />;
    case "agent-skills":
      return <AgentSkillsToolPanel />;
    case "analytics":
      return <AnalyticsToolPanel />;
    case "automations":
      return <AutomationsToolPanel />;
    case "validation":
      return <ValidationToolPanel projectId={projectId} />;
    case "files":
      return <FilesToolPanel projectId={projectId} />;
    default:
      return null;
  }
}

/**
 * Validation commands live in localStorage per project, and each Run
 * fires real sandbox.exec. Output of each run is kept in memory until
 * the tab unmounts (the audit log persists agent-driven runs separately).
 */
function ValidationToolPanel({ projectId }: { projectId: string }) {
  const sandbox = useSandbox(projectId);
  const sandboxRunning =
    sandbox.data?.sandbox?.status === "running" || sandbox.data?.sandbox?.status === "starting";
  const exec = useSandboxExec(projectId);
  const storageKey = `vor:validation:${projectId}`;
  const [tab, setTab] = useState<"commands" | "runs">("commands");
  const [commands, setCommands] = useState<Array<{ id: string; cmd: string }>>(() => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(window.localStorage.getItem(storageKey) ?? "[]") as Array<{ id: string; cmd: string }>;
    } catch {
      return [];
    }
  });
  const [runs, setRuns] = useState<Array<{ id: string; cmd: string; ok: boolean; tail: string; at: number }>>([]);
  const [running, setRunning] = useState<string | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(commands));
    } catch {
      // ignore
    }
  }, [commands, storageKey]);

  async function run(c: { id: string; cmd: string }) {
    setRunning(c.id);
    try {
      const r = await exec.mutateAsync({ command: c.cmd, timeoutMs: 90_000 });
      const tail = (r.stderr || r.stdout || "").trim().split("\n").slice(-8).join("\n");
      setRuns((arr) => [{ id: `r_${Date.now()}`, cmd: c.cmd, ok: r.exitCode === 0, tail, at: Date.now() }, ...arr.slice(0, 19)]);
    } catch (err) {
      setRuns((arr) => [{ id: `r_${Date.now()}`, cmd: c.cmd, ok: false, tail: (err as Error).message, at: Date.now() }, ...arr.slice(0, 19)]);
    } finally {
      setRunning(null);
    }
  }

  return (
    <section style={sectionCard}>
      <div style={{ display: "inline-flex", gap: 4 }}>
        {(["commands", "runs"] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} style={tabBtn(tab === t)}>
            {t === "commands" ? "Commands" : `Runs (${runs.length})`}
          </button>
        ))}
      </div>
      {tab === "commands" ? (
        <>
          {commands.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12, border: "1px dashed var(--border-default)", borderRadius: 8 }}>
              No validation commands configured. Common ones: <code>pnpm test</code>, <code>pnpm typecheck</code>, <code>npm run lint</code>.
            </div>
          ) : (
            <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
              {commands.map((c) => (
                <li key={c.id} style={{ padding: "8px 12px", background: "var(--bg-panel-2)", borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
                  <code style={{ flex: 1, fontFamily: "var(--font-mono)", fontSize: 12 }}>{c.cmd}</code>
                  <button
                    type="button"
                    style={primaryBtnStyle}
                    disabled={!sandboxRunning || running !== null}
                    onClick={() => run(c)}
                  >
                    {running === c.id ? "Running…" : "Run"}
                  </button>
                  <button type="button" style={ghostBtnStyle} onClick={() => setCommands((arr) => arr.filter((x) => x.id !== c.id))}>
                    Remove
                  </button>
                </li>
              ))}
            </ol>
          )}
          <button
            type="button"
            onClick={() => {
              const cmd = prompt("Validation command (e.g. pnpm test)");
              if (cmd?.trim()) setCommands((arr) => [...arr, { id: `c_${Date.now()}`, cmd: cmd.trim() }]);
            }}
            style={primaryBtnStyle}
          >
            + Add command
          </button>
          {!sandboxRunning && (
            <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Start a sandbox to run commands.</div>
          )}
        </>
      ) : (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--text-tertiary)" }}>
            Recent runs
          </div>
          {runs.length === 0 ? (
            <div style={{ padding: 16, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12, border: "1px dashed var(--border-default)", borderRadius: 8 }}>
              No runs yet. Add a command above and hit Run.
            </div>
          ) : (
            <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflow: "auto" }}>
              {runs.map((r) => (
                <li
                  key={r.id}
                  style={{
                    padding: "8px 10px",
                    background: "var(--bg-panel-2)",
                    borderRadius: 8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        color: r.ok ? "#34d399" : "#fca5a5",
                      }}
                    >
                      {r.ok ? "OK" : "FAIL"}
                    </span>
                    <code style={{ flex: 1, fontFamily: "var(--font-mono)", fontSize: 11 }}>{r.cmd}</code>
                    <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{new Date(r.at).toLocaleTimeString()}</span>
                  </div>
                  {r.tail && (
                    <pre style={{ margin: 0, fontFamily: "var(--font-mono)", fontSize: 11, color: r.ok ? "var(--text-secondary)" : "#fca5a5", whiteSpace: "pre-wrap" }}>
                      {r.tail}
                    </pre>
                  )}
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Files finder = client-side filename grep over the project's file list.
 * No fuzzy matching yet; case-insensitive substring is enough for now.
 */
function FilesToolPanel({ projectId }: { projectId: string }) {
  const files = useListProjectFiles(projectId);
  const [q, setQ] = useState("");
  const items = files.data?.items ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((f) => f.path.toLowerCase().includes(needle));
  }, [items, q]);
  return (
    <section style={sectionCard}>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Find a file by path"
        style={inputStyle}
        autoFocus
      />
      <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
        {files.isLoading ? "Loading…" : `${filtered.length} of ${items.length} files`}
      </div>
      <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2, maxHeight: 480, overflow: "auto" }}>
        {filtered.slice(0, 300).map((f) => (
          <li
            key={f.path}
            style={{
              padding: "5px 8px",
              fontFamily: "var(--font-mono)",
              fontSize: 11.5,
              color: "var(--text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {f.path}
          </li>
        ))}
      </ol>
      {filtered.length > 300 && (
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
          (showing first 300 — narrow the query for more)
        </div>
      )}
    </section>
  );
}

function ComingSoonCard({ description }: { description: string }) {
  return (
    <section
      style={{
        border: "1px dashed var(--border-strong)",
        borderRadius: 12,
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: "var(--text-secondary)",
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      <span
        style={{
          alignSelf: "flex-start",
          padding: "3px 10px",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "var(--accent-amber)",
          background: "rgba(245,158,11,0.12)",
          border: "1px solid rgba(245,158,11,0.4)",
          borderRadius: 999,
        }}
      >
        Coming soon
      </span>
      <div>{description}</div>
      <div style={{ color: "var(--text-tertiary)", fontSize: 11 }}>
        Wired to the chrome so the agent + UX know where this lives. Live behavior lands when its
        backing service ships — see <code>HANDOFF.md</code>.
      </div>
    </section>
  );
}

// ---------- Tool panels ----------

const sectionCard: React.CSSProperties = {
  border: "1px solid var(--border-default)",
  background: "var(--bg-panel)",
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const tabBtn = (active: boolean): React.CSSProperties => ({
  padding: "4px 10px",
  background: active ? "var(--bg-active)" : "transparent",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  border: "1px solid " + (active ? "var(--border-strong)" : "transparent"),
  borderRadius: 6,
  fontSize: 12,
  cursor: "pointer",
});

/**
 * Workflows reads scripts from the project's package.json and lets the
 * user run each one inside the sandbox. There's no separate "workflow"
 * model — npm scripts are the source of truth.
 */
function WorkflowsToolPanel({ projectId }: { projectId: string }) {
  const sandbox = useSandbox(projectId);
  const pkgQuery = useReadProjectFile(projectId, "package.json");
  const exec = useSandboxExec(projectId);
  const [running, setRunning] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ name: string; ok: boolean; tail: string } | null>(null);

  const scripts = useMemo(() => {
    if (!pkgQuery.data?.content) return [] as Array<{ name: string; command: string }>;
    try {
      const pkg = JSON.parse(pkgQuery.data.content) as { scripts?: Record<string, string> };
      return Object.entries(pkg.scripts ?? {}).map(([name, command]) => ({ name, command }));
    } catch {
      return [];
    }
  }, [pkgQuery.data?.content]);

  const sandboxRunning =
    sandbox.data?.sandbox?.status === "running" || sandbox.data?.sandbox?.status === "starting";

  async function run(name: string) {
    setRunning(name);
    setLastResult(null);
    try {
      const r = await exec.mutateAsync({ command: `npm run ${name}`, timeoutMs: 90_000 });
      const tail = (r.stderr || r.stdout || "").trim().split("\n").slice(-8).join("\n");
      setLastResult({ name, ok: r.exitCode === 0, tail });
    } catch (err) {
      setLastResult({ name, ok: false, tail: (err as Error).message });
    } finally {
      setRunning(null);
    }
  }

  return (
    <section style={sectionCard}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
          npm scripts from <code style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-tertiary)" }}>package.json</code>
        </div>
        <button type="button" onClick={() => pkgQuery.refetch()} style={ghostBtnStyle}>
          Reload
        </button>
      </div>
      {pkgQuery.isLoading ? (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Reading package.json…</div>
      ) : pkgQuery.error ? (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
          No package.json yet — ask the agent to scaffold one.
        </div>
      ) : scripts.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
          package.json has no <code>scripts</code> block.
        </div>
      ) : (
        <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
          {scripts.map((s) => (
            <li
              key={s.name}
              style={{
                padding: "10px 12px",
                background: "var(--bg-panel-2)",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</span>
                <code style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-tertiary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.command}
                </code>
              </span>
              <button
                type="button"
                onClick={() => run(s.name)}
                disabled={!sandboxRunning || running !== null}
                style={{
                  ...primaryBtnStyle,
                  opacity: !sandboxRunning || running !== null ? 0.5 : 1,
                  cursor: !sandboxRunning || running !== null ? "not-allowed" : "pointer",
                }}
              >
                {running === s.name ? "Running…" : "Run"}
              </button>
            </li>
          ))}
        </ol>
      )}
      {!sandboxRunning && (
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
          Start a sandbox in the top bar to run scripts.
        </div>
      )}
      {lastResult && (
        <pre
          style={{
            margin: 0,
            padding: 12,
            background: "#0a0c10",
            color: lastResult.ok ? "#d1d5db" : "#fca5a5",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            lineHeight: 1.45,
            borderRadius: 8,
            border: "1px solid var(--border-default)",
            whiteSpace: "pre-wrap",
            maxHeight: 200,
            overflow: "auto",
          }}
        >
          <div style={{ color: "var(--text-tertiary)", marginBottom: 6 }}>
            {lastResult.name} — {lastResult.ok ? "ok" : "failed"}
          </div>
          {lastResult.tail || "(no output)"}
        </pre>
      )}
    </section>
  );
}

/**
 * Git panel runs all operations inside the sandbox: status, log, commit,
 * push. Branch + remote are read from `git remote -v` / `git symbolic-ref`.
 * Push needs the user to have set up an SSH key on their git host (see
 * the Developer Tools → SSH keys panel for `ssh-keygen` + the public key).
 */
function GitToolPanel({ projectId }: { projectId: string }) {
  const sandbox = useSandbox(projectId);
  const sandboxRunning =
    sandbox.data?.sandbox?.status === "running" || sandbox.data?.sandbox?.status === "starting";
  const [message, setMessage] = useState("");
  const exec = useSandboxExec(projectId);
  const [busy, setBusy] = useState<"commit" | "push" | null>(null);
  const [lastResult, setLastResult] = useState<{ kind: string; ok: boolean; text: string } | null>(null);

  // git status --porcelain + git log -5 + git symbolic-ref + git remote -v
  // All in one exec for one round-trip; parse the sections out by sentinel.
  const introspect = useLiveSandboxExec(
    projectId,
    `cd /workspace 2>/dev/null && \
echo '---BRANCH---' && git symbolic-ref --short HEAD 2>/dev/null && \
echo '---REMOTE---' && git remote -v 2>/dev/null && \
echo '---STATUS---' && git status --porcelain 2>/dev/null && \
echo '---LOG---' && git log --oneline -10 2>/dev/null`,
    { enabled: sandboxRunning, refetchInterval: 8_000 },
  );

  const parsed = useMemo(() => {
    const out = introspect.data?.stdout ?? "";
    function section(name: string): string {
      const re = new RegExp(`---${name}---\\n([\\s\\S]*?)(?=\\n---|$)`);
      return out.match(re)?.[1]?.trim() ?? "";
    }
    return {
      branch: section("BRANCH") || "(detached)",
      remote: section("REMOTE"),
      statusLines: section("STATUS").split("\n").filter((l) => l.length > 0),
      logLines: section("LOG").split("\n").filter((l) => l.length > 0),
    };
  }, [introspect.data?.stdout]);

  const hasRemote = parsed.remote.length > 0;
  const isGitRepo = (introspect.data?.exitCode ?? 0) === 0 && parsed.branch !== "(detached)";

  async function commit() {
    if (!message.trim()) return;
    setBusy("commit");
    try {
      const safe = message.replace(/'/g, "'\\''");
      const r = await exec.mutateAsync({
        command: `git add -A && git commit -m '${safe}'`,
        timeoutMs: 30_000,
      });
      setLastResult({ kind: "commit", ok: r.exitCode === 0, text: (r.stdout || r.stderr).trim() });
      if (r.exitCode === 0) setMessage("");
      void introspect.refetch();
    } catch (err) {
      setLastResult({ kind: "commit", ok: false, text: (err as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function push() {
    setBusy("push");
    try {
      const r = await exec.mutateAsync({ command: "git push", timeoutMs: 60_000 });
      setLastResult({ kind: "push", ok: r.exitCode === 0, text: (r.stdout || r.stderr).trim() });
      void introspect.refetch();
    } catch (err) {
      setLastResult({ kind: "push", ok: false, text: (err as Error).message });
    } finally {
      setBusy(null);
    }
  }

  if (!sandboxRunning) {
    return (
      <section style={sectionCard}>
        <div style={{ fontSize: 13 }}>Start a sandbox in the top bar to use git.</div>
      </section>
    );
  }
  if (!isGitRepo) {
    return (
      <section style={sectionCard}>
        <div style={{ fontSize: 13 }}>
          /workspace is not a git repo. Initialize:
        </div>
        <button
          type="button"
          onClick={async () => {
            await exec.mutateAsync({ command: "git init -b main && git add -A && git commit -m 'initial commit' --allow-empty" });
            void introspect.refetch();
          }}
          style={primaryBtnStyle}
        >
          git init main
        </button>
      </section>
    );
  }

  return (
    <section style={sectionCard}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Branch</span>
        <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, padding: "2px 6px", background: "var(--bg-panel-2)", borderRadius: 4 }}>
          {parsed.branch}
        </code>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: hasRemote ? "#34d399" : "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>
          {hasRemote ? "Remote connected" : "No remote"}
        </span>
      </div>
      {!hasRemote && (
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
          Add a remote in the terminal: <code style={{ fontFamily: "var(--font-mono)" }}>git remote add origin git@github.com:you/repo.git</code>
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--text-tertiary)" }}>
        Commit
      </div>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Commit message"
        rows={3}
        style={{ ...inputStyle, resize: "vertical" }}
      />
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--text-tertiary)" }}>
        Changes ({parsed.statusLines.length})
      </div>
      {parsed.statusLines.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "8px 10px" }}>
          Working tree clean.
        </div>
      ) : (
        <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2, maxHeight: 160, overflow: "auto" }}>
          {parsed.statusLines.map((l, i) => (
            <li
              key={i}
              style={{
                padding: "4px 10px",
                background: "var(--bg-panel-2)",
                borderRadius: 4,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                whiteSpace: "pre",
              }}
            >
              {l}
            </li>
          ))}
        </ol>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          disabled={!message.trim() || busy !== null || parsed.statusLines.length === 0}
          onClick={commit}
          style={{
            ...primaryBtnStyle,
            padding: "8px 14px",
            opacity: !message.trim() || busy !== null || parsed.statusLines.length === 0 ? 0.5 : 1,
          }}
        >
          {busy === "commit" ? "Committing…" : "Stage + commit all"}
        </button>
        <button
          type="button"
          disabled={!hasRemote || busy !== null}
          onClick={push}
          style={{
            ...ghostBtnStyle,
            padding: "8px 14px",
            opacity: !hasRemote || busy !== null ? 0.5 : 1,
          }}
        >
          {busy === "push" ? "Pushing…" : "Push"}
        </button>
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--text-tertiary)" }}>
        History
      </div>
      {parsed.logLines.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>No commits yet.</div>
      ) : (
        <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
          {parsed.logLines.map((l, i) => (
            <li key={i} style={{ display: "flex", gap: 10, fontSize: 12, fontFamily: "var(--font-mono)" }}>
              <span aria-hidden style={{ color: "var(--accent-blue)" }}>●</span>
              <span style={{ flex: 1, color: "var(--text-secondary)" }}>{l}</span>
            </li>
          ))}
        </ol>
      )}
      {lastResult && (
        <pre
          style={{
            margin: 0,
            padding: 10,
            background: "#0a0c10",
            color: lastResult.ok ? "#d1d5db" : "#fca5a5",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            lineHeight: 1.45,
            borderRadius: 6,
            border: "1px solid var(--border-default)",
            whiteSpace: "pre-wrap",
            maxHeight: 160,
            overflow: "auto",
          }}
        >
          {lastResult.text || `${lastResult.kind} ${lastResult.ok ? "ok" : "failed"}`}
        </pre>
      )}
    </section>
  );
}

function DeveloperToolPanel({ projectId }: { projectId: string }) {
  const [tab, setTab] = useState<"home" | "networking" | "resources" | "ssh">("home");
  if (tab === "home") {
    return (
      <section style={sectionCard}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>Developer Tools</div>
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
          Access debugging tools, monitor resources, and manage your development environment.
        </div>
        {[
          { id: "networking" as const, label: "Networking", sub: "Live port list from the running sandbox" },
          { id: "resources" as const, label: "Resources", sub: "CPU + RAM + load read from /proc" },
          { id: "ssh" as const, label: "SSH keys", sub: "Generate + manage a sandbox ed25519 keypair for git push" },
        ].map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => setTab(row.id)}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              padding: "12px 14px",
              border: "1px solid var(--border-default)",
              background: "var(--bg-panel-2)",
              borderRadius: 10,
              color: "var(--text-primary)",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 500 }}>{row.label}</span>
            <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{row.sub}</span>
          </button>
        ))}
      </section>
    );
  }
  return (
    <section style={{ ...sectionCard, gap: 14 }}>
      <button type="button" onClick={() => setTab("home")} style={ghostBtnStyle}>
        ← Developer Tools
      </button>
      {tab === "networking" && <NetworkingPanel projectId={projectId} />}
      {tab === "resources" && <ResourcesPanel projectId={projectId} />}
      {tab === "ssh" && <SshKeysPanel projectId={projectId} />}
    </section>
  );
}

/**
 * Live port table from `ss -tulpn` inside the sandbox. Refreshes every
 * 5s. The dev URL shown above is the api-server's live preview proxy
 * (chunk 5a) — same URL the canvas iframe loads.
 */
function NetworkingPanel({ projectId }: { projectId: string }) {
  const sandbox = useSandbox(projectId);
  const machineId = sandbox.data?.sandbox?.flyMachineId ?? "";
  const sandboxRunning =
    sandbox.data?.sandbox?.status === "running" || sandbox.data?.sandbox?.status === "starting";
  const ports = useLiveSandboxExec(
    projectId,
    "ss -tulpn 2>/dev/null | tail -n +2 || netstat -tulpn 2>/dev/null | tail -n +3",
    { enabled: sandboxRunning },
  );

  const rows = useMemo(() => parsePortLines(ports.data?.stdout ?? ""), [ports.data?.stdout]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>Networking</div>
      <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
        Live preview reaches the sandbox at port 3000 by default. Pass <code>?vorPort=N</code> on the iframe URL to switch.
      </div>
      <code
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          padding: "8px 10px",
          background: "var(--bg-canvas)",
          border: "1px solid var(--border-default)",
          borderRadius: 7,
        }}
      >
        https://api.vor.codes/api/projects/{projectId}/preview/live/
      </code>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--text-tertiary)" }}>
        Open ports ({rows.length})
      </div>
      {!sandboxRunning ? (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Start a sandbox to see live ports.</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
          Nothing listening yet. In the terminal run a dev server, e.g. <code>npm run dev -- --host 0.0.0.0</code>.
        </div>
      ) : (
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: "var(--text-tertiary)" }}>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Proto</th>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Address</th>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Process</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.address}-${i}`}>
                <td style={{ padding: "4px 6px", fontFamily: "var(--font-mono)" }}>{r.proto}</td>
                <td style={{ padding: "4px 6px", fontFamily: "var(--font-mono)" }}>{r.address}</td>
                <td style={{ padding: "4px 6px", color: "var(--text-tertiary)" }}>{r.process || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {machineId && (
        <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
          machine <code style={{ fontFamily: "var(--font-mono)" }}>{machineId}</code>
        </div>
      )}
    </div>
  );
}

/** Parse `ss -tulpn` output: tcp LISTEN 0 511 0.0.0.0:3000 0.0.0.0:* users:(("node",pid=42,fd=20)) */
function parsePortLines(stdout: string): Array<{ proto: string; address: string; process: string }> {
  const out: Array<{ proto: string; address: string; process: string }> = [];
  for (const raw of stdout.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const parts = line.split(/\s+/);
    if (parts.length < 5) continue;
    const proto = parts[0]!.toLowerCase();
    if (proto !== "tcp" && proto !== "udp" && proto !== "tcp6" && proto !== "udp6") continue;
    const address = parts[4] ?? "";
    const m = line.match(/users:\(\("([^"]+)",pid=(\d+)/);
    const process = m ? `${m[1]} (pid ${m[2]})` : "";
    out.push({ proto, address, process });
  }
  return out;
}

/**
 * Resources panel pulls live /proc stats from the sandbox.
 *   - mem: /proc/meminfo
 *   - load: /proc/loadavg
 *   - cpus: nproc
 */
function ResourcesPanel({ projectId }: { projectId: string }) {
  const sandbox = useSandbox(projectId);
  const sandboxRunning =
    sandbox.data?.sandbox?.status === "running" || sandbox.data?.sandbox?.status === "starting";
  const live = useLiveSandboxExec(
    projectId,
    "nproc 2>/dev/null && cat /proc/loadavg 2>/dev/null && cat /proc/meminfo 2>/dev/null | head -5",
    { enabled: sandboxRunning },
  );

  const parsed = useMemo(() => parseResources(live.data?.stdout ?? ""), [live.data?.stdout]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>Resources</span>
        <span
          style={{
            padding: "2px 8px",
            borderRadius: 999,
            background: "rgba(99,102,241,0.18)",
            color: "#a5b4fc",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.4,
          }}
        >
          shared-cpu-1x · 256 MB
        </span>
      </div>
      {!sandboxRunning ? (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Start a sandbox to see live stats.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            { label: "CPUs", value: parsed.cpus ?? "—" },
            { label: "Load (1m)", value: parsed.load1 ?? "—" },
            { label: "Load (5m)", value: parsed.load5 ?? "—" },
            { label: "Load (15m)", value: parsed.load15 ?? "—" },
            { label: "Memory total", value: parsed.memTotal ?? "—" },
            { label: "Memory available", value: parsed.memAvailable ?? "—" },
          ].map((r) => (
            <div key={r.label} style={{ padding: "10px 12px", border: "1px solid var(--border-default)", borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{r.label}</div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{r.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function parseResources(stdout: string): {
  cpus?: string;
  load1?: string;
  load5?: string;
  load15?: string;
  memTotal?: string;
  memAvailable?: string;
} {
  const lines = stdout.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return {};
  const cpus = /^\d+$/.test(lines[0] ?? "") ? lines[0] : undefined;
  const loadLine = (cpus ? lines[1] : lines[0]) ?? "";
  const loadParts = loadLine.split(/\s+/);
  const [load1, load5, load15] = [loadParts[0], loadParts[1], loadParts[2]];
  let memTotal: string | undefined;
  let memAvailable: string | undefined;
  for (const l of lines) {
    if (l.startsWith("MemTotal:")) memTotal = formatMemKb(l);
    else if (l.startsWith("MemAvailable:")) memAvailable = formatMemKb(l);
  }
  const out: ReturnType<typeof parseResources> = {};
  if (cpus) out.cpus = cpus;
  if (load1) out.load1 = load1;
  if (load5) out.load5 = load5;
  if (load15) out.load15 = load15;
  if (memTotal) out.memTotal = memTotal;
  if (memAvailable) out.memAvailable = memAvailable;
  return out;
}

function formatMemKb(line: string): string {
  const m = line.match(/(\d+)\s*kB/i);
  if (!m) return line;
  const kb = Number(m[1]);
  if (kb >= 1024 * 1024) return `${(kb / 1024 / 1024).toFixed(1)} GB`;
  if (kb >= 1024) return `${(kb / 1024).toFixed(0)} MB`;
  return `${kb} kB`;
}

/**
 * SSH keys panel: generates an ed25519 keypair inside the sandbox at
 * /workspace/.vor/id_ed25519 + .pub. Surfaces the public key with a
 * copy button so the user can paste it into their GitHub / GitLab
 * profile. `git push` then works over SSH from the sandbox terminal.
 *
 * The private key never leaves the sandbox.
 */
function SshKeysPanel({ projectId }: { projectId: string }) {
  const sandbox = useSandbox(projectId);
  const sandboxRunning =
    sandbox.data?.sandbox?.status === "running" || sandbox.data?.sandbox?.status === "starting";
  const exec = useSandboxExec(projectId);
  const probe = useLiveSandboxExec(
    projectId,
    "cat /workspace/.vor/id_ed25519.pub 2>/dev/null",
    { enabled: sandboxRunning, refetchInterval: 0 },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const publicKey = (probe.data?.stdout ?? "").trim();

  async function generate() {
    setError(null);
    setBusy(true);
    try {
      const r = await exec.mutateAsync({
        command:
          "mkdir -p /workspace/.vor && ssh-keygen -t ed25519 -f /workspace/.vor/id_ed25519 -N '' -C vor-sandbox -q && " +
          "mkdir -p ~/.ssh && cp /workspace/.vor/id_ed25519 ~/.ssh/id_ed25519 && chmod 600 ~/.ssh/id_ed25519 && " +
          "cat /workspace/.vor/id_ed25519.pub",
        timeoutMs: 30_000,
      });
      if (r.exitCode !== 0) setError(r.stderr.trim() || "ssh-keygen failed");
      void probe.refetch();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    setBusy(true);
    try {
      await exec.mutateAsync({ command: "rm -f /workspace/.vor/id_ed25519 /workspace/.vor/id_ed25519.pub ~/.ssh/id_ed25519" });
      void probe.refetch();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>SSH key for this sandbox</div>
      <div style={{ fontSize: 12, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
        Add the public key to GitHub <code>Settings → SSH and GPG keys</code> (or GitLab equivalent),
        then <code>git remote add origin git@github.com:you/repo.git</code> and the Git panel's Push button works.
      </div>
      {!sandboxRunning ? (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Start a sandbox to manage keys.</div>
      ) : publicKey ? (
        <>
          <pre
            style={{
              margin: 0,
              padding: 10,
              background: "var(--bg-canvas)",
              border: "1px solid var(--border-default)",
              borderRadius: 7,
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              lineHeight: 1.45,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {publicKey}
          </pre>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(publicKey);
              }}
              style={primaryBtnStyle}
            >
              Copy public key
            </button>
            <button type="button" onClick={revoke} disabled={busy} style={ghostBtnStyle}>
              {busy ? "Working…" : "Revoke"}
            </button>
          </div>
        </>
      ) : (
        <button type="button" onClick={generate} disabled={busy} style={primaryBtnStyle}>
          {busy ? "Generating…" : "Generate ed25519 keypair"}
        </button>
      )}
      {error && (
        <div style={{ fontSize: 11, color: "var(--accent-red)" }}>{error}</div>
      )}
    </div>
  );
}

/**
 * Code search runs `grep -rn` inside the sandbox. Submitting blocks the
 * UI for up to 30s. Excludes node_modules / .git / dist by default.
 */
function CodeSearchToolPanel({ projectId }: { projectId: string }) {
  const sandbox = useSandbox(projectId);
  const sandboxRunning =
    sandbox.data?.sandbox?.status === "running" || sandbox.data?.sandbox?.status === "starting";
  const exec = useSandboxExec(projectId);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<{ ran: boolean; tooMany: boolean; lines: string[]; error: string | null }>({
    ran: false,
    tooMany: false,
    lines: [],
    error: null,
  });
  const [busy, setBusy] = useState(false);

  async function search() {
    const term = q.trim();
    if (!term) return;
    setBusy(true);
    try {
      // Escape single quotes so the regex stays inside one shell-quoted arg.
      const safe = term.replace(/'/g, "'\\''");
      const cmd =
        `cd /workspace && grep -rnE --color=never ` +
        `--exclude-dir=node_modules --exclude-dir=.git --exclude-dir=dist --exclude-dir=build ` +
        `'${safe}' . 2>/dev/null | head -200`;
      const r = await exec.mutateAsync({ command: cmd, timeoutMs: 30_000 });
      const lines = r.stdout.split("\n").filter((l) => l.length > 0);
      setResults({ ran: true, tooMany: lines.length >= 200, lines, error: null });
    } catch (err) {
      setResults({ ran: true, tooMany: false, lines: [], error: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={sectionCard}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
        style={{ display: "flex", gap: 8 }}
      >
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Regex or literal — searched across all project files (excludes node_modules)"
          autoFocus
          style={{ ...inputStyle, flex: 1 }}
        />
        <button
          type="submit"
          disabled={!q.trim() || busy || !sandboxRunning}
          style={{
            ...primaryBtnStyle,
            opacity: !q.trim() || busy || !sandboxRunning ? 0.5 : 1,
          }}
        >
          {busy ? "Searching…" : "Search"}
        </button>
      </form>
      {!sandboxRunning && (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
          Start a sandbox in the top bar to search source files.
        </div>
      )}
      {results.error && (
        <div style={{ fontSize: 12, color: "var(--accent-red)" }}>{results.error}</div>
      )}
      {results.ran && !results.error && (
        <>
          <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
            {results.lines.length} matches{results.tooMany ? " (truncated at 200)" : ""}
          </div>
          {results.lines.length === 0 ? (
            <div style={{ padding: 16, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12 }}>
              0 matches.
            </div>
          ) : (
            <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2, maxHeight: 360, overflow: "auto" }}>
              {results.lines.map((l, i) => (
                <li
                  key={i}
                  style={{
                    padding: "4px 8px",
                    background: "var(--bg-panel-2)",
                    borderRadius: 4,
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    whiteSpace: "pre",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={l}
                >
                  {l}
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </section>
  );
}

function VncToolPanel() {
  return (
    <section style={{ ...sectionCard, alignItems: "center", padding: 40, textAlign: "center" }}>
      <div style={{ fontSize: 32 }}>🖥️</div>
      <div style={{ fontSize: 14, fontWeight: 600 }}>View your app's desktop screen output</div>
      <div style={{ fontSize: 12, color: "var(--text-tertiary)", maxWidth: 480 }}>
        No VNC server is currently running with compatible ports available to view. Try running your
        App, and check the Networking tool to ensure ports are being forwarded correctly.
      </div>
    </section>
  );
}

/**
 * Past releases — currently surfaces the EAS Build history (the only
 * publish primitive that's real today). Web/static publish history
 * lands when the publish_store table is wired through a list endpoint.
 */
function PublishingToolPanel({ projectId }: { projectId: string }) {
  const builds = useProjectBuilds(projectId);
  const items = builds.data?.items ?? [];
  return (
    <section style={sectionCard}>
      <div style={{ fontSize: 13, fontWeight: 500 }}>Recent builds + releases</div>
      <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
        EAS Build history is the source of truth for store-bound artifacts. Web publish history will appear here when the publish store ships its list endpoint.
      </div>
      {items.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            border: "1px dashed var(--border-default)",
            borderRadius: 10,
            color: "var(--text-tertiary)",
            fontSize: 12,
          }}
        >
          No builds yet. Trigger one from the right-panel <strong>Preview on your phone</strong> tab.
        </div>
      ) : (
        <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
          {items.slice(0, 20).map((b) => (
            <li
              key={b.id}
              style={{
                padding: "8px 12px",
                background: "var(--bg-panel-2)",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 12 }}>{b.platform}</span>
              <code style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-tertiary)" }}>
                {b.expoBuildId.slice(0, 7)}
              </code>
              <span style={{ flex: 1, fontSize: 11, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.3 }}>
                {b.status}
              </span>
              <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                {new Date(b.createdAt).toLocaleString()}
              </span>
              {b.artifactUrl && (
                <a
                  href={b.artifactUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--accent-blue)", fontSize: 11, textDecoration: "none" }}
                >
                  Download
                </a>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function IntegrationsToolPanel() {
  return (
    <ComingSoonCard description="Per-project credentials for Stripe / Slack / Linear / SendGrid. Right now project secrets live in the platform-side .env passed to the sandbox at boot. The integrations marketplace lands alongside Stripe checkout in Phase 3." />
  );
}

/**
 * App storage = the project's own files in R2. We surface only the
 * image-ish + asset-ish files because the file tree already covers code.
 */
function AppStorageToolPanel({ projectId }: { projectId: string }) {
  const files = useListProjectFiles(projectId, { prefix: "assets/" });
  const items = files.data?.items ?? [];
  return (
    <section style={sectionCard}>
      <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
        Files under <code>assets/</code> in this project's source bucket — images, fonts, blobs the agent writes.
      </div>
      {items.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            border: "1px dashed var(--border-default)",
            borderRadius: 10,
            color: "var(--text-tertiary)",
            fontSize: 12,
          }}
        >
          No assets yet. The agent can save generated images here, or drag-and-drop will land in a follow-up.
        </div>
      ) : (
        <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((f) => (
            <li
              key={f.path}
              style={{
                padding: "8px 12px",
                background: "var(--bg-panel-2)",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 12,
              }}
            >
              <span style={{ flex: 1, fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.path}
              </span>
              <span style={{ color: "var(--text-tertiary)", fontSize: 11 }}>
                {formatBytes(f.size)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function AuthToolPanel() {
  return (
    <ComingSoonCard description="Configure how end-users sign into the app you're building. Right now platform sign-in (yours, to use Vor) is real — magic-link + Google + GitHub — but the generated app's own auth surface needs a scaffolded @vorloop/auth-client setup that lands with the per-project DB connection in Phase 3." />
  );
}

function SecurityToolPanel() {
  return (
    <ComingSoonCard description="`npm audit` + lint surface for the project. The data lives in the sandbox; this panel will run the audit on demand and render the findings. Implementation parity with the Validation panel — landing in the next batch." />
  );
}

function AgentSkillsToolPanel() {
  return (
    <ComingSoonCard description="Marketplace of domain-specific tools the agent can install at runtime (Stripe, Sentry, Auth0, etc.). The agent's core tool catalog is fixed today; pluggable skills + per-project policy land alongside Phase 3 billing." />
  );
}

function AnalyticsToolPanel() {
  return (
    <ComingSoonCard description="Per-project traffic + error metrics. We have Cloudflare Pages analytics on the platform itself but no per-project rollups yet — wire targets after the publish_store list endpoint ships." />
  );
}

function AutomationsToolPanel() {
  return (
    <ComingSoonCard description="Cron + webhook-triggered runs against the sandbox. Needs a persistent scheduler that survives sandbox idle sweeps — overlaps with Phase 4 background-turn queue work." />
  );
}

// ---------- shared mini-styles ----------

const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  background: "var(--bg-canvas)",
  border: "1px solid var(--border-default)",
  borderRadius: 7,
  color: "var(--text-primary)",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  outline: "none",
  width: "100%",
};

const primaryBtnStyle: React.CSSProperties = {
  padding: "6px 12px",
  background: "var(--accent-blue)",
  color: "#fff",
  border: "none",
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
};

function planFor(id: string): string {
  switch (id) {
    case "publishing":
      return "A dedicated publish workflow — pick a slug, choose visibility, snapshot the build, get a shareable URL. Distinct from the top-bar Publish button: this is where you manage past releases.";
    case "integrations":
      return "Connect third-party services (Slack, Stripe, Linear, SendGrid). Per-project credentials with revoke and rotate.";
    case "app-storage":
      return "Per-project blob storage for user uploads. List, preview, delete, get-public-URL.";
    case "auth":
      return "Configure auth for the generated app: enable providers, edit the login screen copy, manage signed-in users.";
    case "security":
      return "Static analysis + dep audit results. Surfaces npm-audit + a Vor linter that catches common issues (leaked secrets, unsigned auth handlers).";
    case "agent-skills":
      return "Marketplace of agent extensions: domain-specific tools (Stripe, Auth0, Sentry) that show up as additional callable tools in the agent loop.";
    case "analytics":
      return "Traffic + usage charts for published apps. Visits, top routes, error rate, build minutes spent.";
    case "automations":
      return "Cron and event-triggered runs. Schedule the agent to do a thing, or wire a webhook to trigger a workflow.";
    case "canvas":
      return "Agent-controlled canvas for free-form wireframes that aren't constrained to a phone or browser frame. Useful for sketching screens before code.";
    case "code-search":
      return "Full-text search across all project files with regex + path filters.";
    case "developer":
      return "Internal diagnostics: build times, token usage, recent errors. Useful when something's wedged.";
    case "git":
      return "Version control panel: list commits, see diffs, push/pull. Replit-style — no terminal git commands required for the common cases.";
    case "vnc":
      return "VNC view of the sandbox's desktop. Handy for Electron / Tauri builds that need a screen.";
    case "workflows":
      return "Pick how the project starts — `pnpm dev`, `expo start`, custom. Multiple configs per project for different scenarios.";
    default:
      return "Detailed scope ships in the spec; tool surface lands when the backing service ships.";
  }
}

function DesignPlaceholder({ tab }: { tab: CenterTab }) {
  const content =
    tab === "design"
      ? {
          title: "Design specs",
          subtitle:
            "Inspect tokens, type scale, spacing, and component variants used by your project.",
        }
      : {
          title: "Tools & files",
          subtitle:
            "Project-level utilities: package.json viewer, environment variables, migrations.",
        };
  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        color: "var(--text-tertiary)",
        fontSize: 12,
        padding: 24,
        textAlign: "center",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          border: "1px solid var(--border-strong)",
          background: "var(--bg-panel)",
          display: "grid",
          placeItems: "center",
          color: "var(--text-secondary)",
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      </div>
      <div style={{ color: "var(--text-secondary)", fontSize: 14, fontWeight: 500 }}>
        {content.title}
      </div>
      <div style={{ maxWidth: 360, lineHeight: 1.55 }}>{content.subtitle}</div>
      <div
        style={{
          marginTop: 4,
          padding: "4px 10px",
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "var(--accent-amber)",
          background: "rgba(245,158,11,0.10)",
          border: "1px solid rgba(245,158,11,0.35)",
          borderRadius: 999,
        }}
      >
        Coming soon
      </div>
    </div>
  );
}
